import BreadcrumbList from '@components/BreadcrumbList';
import ProceedCheckout from '@components/ProceedCheckout';
import CheckoutCTA from '@components/CheckoutCTA';
import { useAuthentication } from '@context/businessContainer';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import {
  format,
  formatPrismicData,
  formatWeight,
} from '@monofront/business/utils';
import Close from '@monofront/design-system/visuals/Icons/Old/Close';
import ShoppingCartOutline from '@monofront/design-system/visuals/Icons/Old/ShoppingCartOutline';
import Button from '@monofront/design-system/components/Button';
import {
  HighlightedValue,
  LineWithValue,
  SecuredPayment,
  Summary,
} from '@monofront/design-system/organisms/Summary';
import { getQueryParam } from '@utils/query';
import { useEffect, useState, type ReactNode, type FC } from 'react';
import Items from './Items';
import ProgressFreeDelivery from './ProgressFreeDelivery';
import { useTranslations } from 'next-intl';
import OurGuarantees, {
  useGetGuaranteesPreset,
} from '@components/OurGuarantees';
import useI18n from '@hooks/i18n/useI18n';
import { useMediaQuery, useSessionStorage } from '@monofront/business/hooks';
import { SESSIONSTORAGE_ROUTE_URL_BEFORE_CART_PAGE } from '@constants/sessionStorage';
import { useRouter } from 'next/router';
import { useCartMaxWeight } from '@hooks/useCartMaxWeight';
import PaypalPayLaterMessage from './PaypalPayLaterMessage';
import { useFeatureIsOn, useFeatureValue } from '@monofront/business/contexts';
import { IPrismicCartSharedContents } from '@monofront/types';
import {
  EXPIRED_CARD_BANNER,
  ABTEST_CHECKOUT_PAYMENT_MAAP_BLOCK,
  FEATURE_FLAG_PAYPAL_MAAP,
  REDIRECTION_TO_SUGGESTIONS_PAGE_RULE,
  ABTEST_DELIVERY_BAR,
} from '@monofront/business/constants';
import Link from 'next/link';
import AddressSummary from '@features/Fresh/AddressSummary';
import { trackEvent } from '@monofront/tracking';
import { useSubscribeInCheckout } from '@monofront/business/hooks';
import { CreditCardStatusBanner } from '@components/Layout/Header/Banners';
import { createPortal } from 'react-dom';
import { useLayoutRefs } from '@context/layoutRefs';
import ProgressBarByShippingMethod from './ProgressBarByShippingMethod';
import { useTagContextMatches } from '@hooks/businessContainer';

interface IShoppingCartProps {
  children: ReactNode;
  headerRender?: ReactNode;
  prismicCartSharedContents: IPrismicCartSharedContents;
}

const ShoppingCartHeader = ({ totalQuantity, fromPage, onClose }) => {
  const tCart = useTranslations('Cart');

  return (
    <>
      <div className="title-block">
        <div className="tw-flex tw-items-center tw-space-x-4 tw-px-4 lg:tw-px-0">
          <p className="tw-relative">
            <ShoppingCartOutline width={24} height={24} aria-hidden="true" />
            <span className="sr-only">{tCart('articleQuantity')}</span>
            {totalQuantity > 0 && (
              <span className="tw-absolute tw--right-1.5 tw--top-1.5 tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-bg-yellow-600 tw-text-center tw-text-[10px] tw-font-semibold">
                {totalQuantity}
              </span>
            )}
          </p>
          <h1 className="title-xs cart-link tw-text-[22px]">
            {fromPage === 'prefilled-cart'
              ? tCart('myPersonalizedCart')
              : tCart('myNormalCart')}
          </h1>
        </div>

        <button
          className="close-cart mobile-only"
          type="button"
          onClick={onClose}
          data-testid="Cart-button-close-cart"
        >
          <Close width={12} height={12} />
        </button>
      </div>
      <style jsx>{`
        .title-block {
          display: grid;
          grid-template-columns: 50% 50%;
          flex-direction: row;
          margin-bottom: var(--space-5);
        }

        .title-block > h1 {
          font-size: var(--font-size-4);
          font-weight: var(--font-weight-bold);
        }

        @media screen and (max-width: 800px) {
          .title-block {
            text-align: center;
            padding: var(--space-4) 0;
            position: sticky;
            top: 0;
            background: var(--white);
            margin-bottom: 0;
            width: 100%;
            grid-template-columns: 1fr;
            grid-template-areas: 'title close-cart';
            border-bottom: 1px solid var(--clr-tertiary-2);
          }

          .title-block > h1 {
            grid-area: title;
            text-transform: capitalize;
            font-size: var(--font-size-2);
          }

          .close-cart {
            grid-area: close-cart;
            display: block;
            position: absolute;
            top: 5px;
            right: var(--space-5);
            border: none;
            background-color: transparent;
          }
        }
      `}</style>
    </>
  );
};

const useIsRehydrated = () => {
  const [isRehydrated, setIsRehydrated] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsRehydrated(true);
    }
  }, []);

  return isRehydrated;
};

const ShoppingCart: FC<IShoppingCartProps> = ({
  children,
  headerRender,
  prismicCartSharedContents,
}) => {
  const { headerAddonsRef } = useLayoutRefs();
  const router = useRouter();
  const [routeUrlBeforeCartPage] = useSessionStorage(
    SESSIONSTORAGE_ROUTE_URL_BEFORE_CART_PAGE,
    '/home',
  );
  const { shoppingCart, shoppingCartId, refreshProducts } = useShoppingCart();
  const isPaypalSubscriptionOn = useFeatureIsOn(FEATURE_FLAG_PAYPAL_MAAP);
  const redirectionToSuggestionsPageRule = useFeatureValue(
    ...REDIRECTION_TO_SUGGESTIONS_PAGE_RULE,
  );
  const { isMember } = useAuthentication();
  const fromPage = getQueryParam('from');
  const isCartNotEmpty = shoppingCart.products?.length > 0;
  const maxWeight = useCartMaxWeight();
  const { isSubscriptionAvailable, isLoading } =
    useSubscribeInCheckout(useAuthentication);
  const isMobile = useMediaQuery(800);
  const isMAAPBlockAbTestEnabled = useFeatureIsOn(
    ABTEST_CHECKOUT_PAYMENT_MAAP_BLOCK,
  );
  const ourGuaranteesPreset = useGetGuaranteesPreset();
  const isDeliveryBarABTestEnabled = useFeatureIsOn(ABTEST_DELIVERY_BAR);

  const { isGermanMarket } = useI18n();
  const tCart = useTranslations('Cart');
  const tAccount = useTranslations('Account');
  const tPayment = useTranslations('Payment');
  // FIXME: fixed in !1994
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const cartSharedContents = formatPrismicData(prismicCartSharedContents);

  const { tagContextMatches } = useTagContextMatches({ isWebMobile: isMobile });

  const shouldRedirectToSuggestionsPage = tagContextMatches(
    redirectionToSuggestionsPageRule,
  );

  const isExpiredCardBannerEnabled = useFeatureIsOn(EXPIRED_CARD_BANNER);

  useEffect(() => {
    document.body.classList.remove('no-overflow');

    if (!isLoading) {
      trackEvent({
        type: 'LFPageView',
        data: {
          isMaap: isSubscriptionAvailable,
        },
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (shoppingCart.products?.length > 0) {
      refreshProducts();
    }
  }, [shoppingCart.products]);

  const totalPrice =
    isGermanMarket && shoppingCart.totalPfand
      ? shoppingCart.totalPrice + shoppingCart.totalPfand
      : shoppingCart.totalPrice;

  const isRehydrated = useIsRehydrated();

  if (!isRehydrated) {
    return null;
  }

  return (
    <div className="limited-width-container">
      <BreadcrumbList name="Votre panier" />
      <div className="container">
        <div className="cart-container">
          <div className="cart tw-gap-x-[72px]">
            <div className="content">
              {isMobile &&
                headerAddonsRef.current &&
                createPortal(
                  <ShoppingCartHeader
                    totalQuantity={shoppingCart.totalQuantity}
                    fromPage={fromPage}
                    onClose={() => router.push(routeUrlBeforeCartPage)}
                  />,
                  headerAddonsRef.current,
                )}
              {!isMobile && (
                <ShoppingCartHeader
                  totalQuantity={shoppingCart.totalQuantity}
                  fromPage={fromPage}
                  onClose={() => router.push(routeUrlBeforeCartPage)}
                />
              )}
              {isExpiredCardBannerEnabled && isMobile && (
                <CreditCardStatusBanner />
              )}
              {isDeliveryBarABTestEnabled ? (
                isCartNotEmpty ? null : (
                  <ProgressBarByShippingMethod UNSAFE_className="tw-p-4" />
                )
              ) : (
                <AddressSummary />
              )}
              {isCartNotEmpty && (
                <>
                  <div className="tw-p-4 lg:tw-hidden">
                    {isDeliveryBarABTestEnabled ? (
                      <ProgressBarByShippingMethod />
                    ) : (
                      <ProgressFreeDelivery
                        totalPrice={shoppingCart.totalPrice}
                      />
                    )}
                  </div>

                  <div className="mobile-only tw-mx-4 md:tw-mb-4">
                    {!!shoppingCart?.totalPfand && (
                      <div className="tw-mb-2">
                        <LineWithValue
                          label={<span>{tCart('includingPfand')}</span>}
                          value={format(shoppingCart.totalPfand)}
                        />
                      </div>
                    )}
                    <div className="tw-mb-2">
                      <LineWithValue
                        label={
                          <span>
                            {tCart.rich('weightOfCart', {
                              emoji: (children) => (
                                <span role="img" aria-hidden="true">
                                  {children}
                                </span>
                              ),
                            })}
                          </span>
                        }
                        value={formatWeight(
                          shoppingCart.totalWeight?.toFixed(2) || 0,
                        )}
                      />
                    </div>

                    <LineWithValue
                      label={tAccount('mySavings')}
                      value={
                        <HighlightedValue>
                          {format(shoppingCart.totalEconomy || 0)}
                        </HighlightedValue>
                      }
                    />
                  </div>
                </>
              )}
              {headerRender ? (
                <div className="items-top">{headerRender}</div>
              ) : null}
              <div className="products">
                {shoppingCartId && (
                  <Items
                    shoppingCartId={shoppingCartId}
                    shoppingCart={shoppingCart}
                    // FIXME: fixed in !1994
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    cartSharedContents={cartSharedContents}
                  />
                )}
              </div>
            </div>
            <div className="global-information">
              {shoppingCart ? (
                <div className="right-column">
                  <Summary
                    UNSAFE_className={`summary-container ${
                      isMAAPBlockAbTestEnabled ? 'tw-rounded-lg' : ''
                    }`}
                  >
                    <>
                      <div className="tw-space-y-4">
                        {isDeliveryBarABTestEnabled ? (
                          <ProgressBarByShippingMethod />
                        ) : (
                          <ProgressFreeDelivery
                            totalPrice={shoppingCart.totalPrice}
                          />
                        )}
                        <LineWithValue
                          label={tCart(
                            shoppingCart.totalQuantity === 1
                              ? 'subTotalProduct'
                              : 'subTotalProducts',
                            { nb: shoppingCart.totalQuantity },
                          )}
                          value={format(shoppingCart.subtotal || 0)}
                        />
                        {isGermanMarket && shoppingCart.totalPfand ? (
                          <LineWithValue
                            label={tCart('pfand')}
                            value={format(shoppingCart.totalPfand)}
                          />
                        ) : null}
                        {shoppingCart.promotion !== 0 && (
                          <LineWithValue
                            label={tCart('discount')}
                            value={format(shoppingCart.promotion)}
                          />
                        )}

                        <LineWithValue
                          label={tCart('total')}
                          value={format(totalPrice || 0)}
                          isImportant
                        />
                      </div>
                      <div className="tw-my-6">
                        {shouldRedirectToSuggestionsPage ? (
                          <Link href="/pages/suggestions" legacyBehavior>
                            <Button variant="primary">
                              {tCart('continueToDelivery')}
                            </Button>
                          </Link>
                        ) : (
                          <ProceedCheckout
                            paymentButtonLabel={tCart('continueToDelivery')}
                            disabledButton={
                              shoppingCart.totalPrice === 0 ||
                              shoppingCart.totalWeight >= maxWeight
                            }
                            dataTestId="ProceedCheckout-Button-go-to-checkout-delivery"
                          />
                        )}

                        {isMAAPBlockAbTestEnabled &&
                          shoppingCart.totalWeight >= maxWeight && (
                            <p className="tw-lf-body-bold-sm tw-mt-4 tw-text-center tw-text-red-600">
                              {tCart('weightLimitWarning', { maxWeight })}
                            </p>
                          )}
                      </div>
                      {!isMAAPBlockAbTestEnabled && (
                        <>
                          <div className="tw-mb-6 tw-space-y-2">
                            <LineWithValue
                              label={
                                <span>
                                  {tCart.rich('weightOfCart', {
                                    emoji: (children) => (
                                      <span role="img" aria-hidden="true">
                                        {children}
                                      </span>
                                    ),
                                  })}
                                </span>
                              }
                              value={formatWeight(
                                shoppingCart.totalWeight?.toFixed(2) || 0,
                              )}
                            />
                            <LineWithValue
                              label={tAccount('mySavings')}
                              value={
                                <HighlightedValue>
                                  {format(shoppingCart.totalEconomy || 0)}
                                </HighlightedValue>
                              }
                            />
                          </div>

                          {shoppingCart.totalWeight >= maxWeight && (
                            <div className="error-centered">
                              <p className="error tw-lf-error-message">
                                {tCart('weightLimitWarning', { maxWeight })}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                    {isMAAPBlockAbTestEnabled && (
                      <div className="tw-my-6 tw-space-y-2">
                        <LineWithValue
                          label={
                            <span>
                              {tCart.rich('weightOfCart', {
                                emoji: (children) => (
                                  <span role="img" aria-hidden="true">
                                    {children}
                                  </span>
                                ),
                              })}
                            </span>
                          }
                          value={formatWeight(
                            shoppingCart.totalWeight?.toFixed(2) || 0,
                          )}
                        />
                        <LineWithValue
                          label={tAccount('mySavings')}
                          value={
                            <HighlightedValue>
                              {format(shoppingCart.totalEconomy || 0)}
                            </HighlightedValue>
                          }
                        />
                      </div>
                    )}
                    <div className="tw-mt-6">
                      {(isMember || isPaypalSubscriptionOn) &&
                        totalPrice != null && (
                          <PaypalPayLaterMessage amount={totalPrice} />
                        )}
                      <SecuredPayment label={tPayment('safePayment')} />
                    </div>
                  </Summary>

                  <OurGuarantees
                    preset={ourGuaranteesPreset}
                    isShoppingCartFUF={
                      shoppingCart.hasFreshProduct ||
                      shoppingCart.hasUltraFreshProduct
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          {isCartNotEmpty && shoppingCart.products && (
            <>
              {!isMember && (
                <div className="mobile-only guarantees tw-mt-8">
                  <OurGuarantees
                    preset={ourGuaranteesPreset}
                    isShoppingCartFUF={
                      shoppingCart.hasFreshProduct ||
                      shoppingCart.hasUltraFreshProduct
                    }
                  />
                </div>
              )}
              <div className="payment-info-mobile mobile-only">
                {(isMember || isPaypalSubscriptionOn) && totalPrice != null && (
                  <PaypalPayLaterMessage amount={totalPrice} />
                )}
                {isMember && <SecuredPayment label={tPayment('safePayment')} />}
              </div>
              <div className="recommendations">{children}</div>
              {isMember && (
                <div className="mobile-only guarantees">
                  <OurGuarantees
                    preset={ourGuaranteesPreset}
                    isShoppingCartFUF={
                      shoppingCart.hasFreshProduct ||
                      shoppingCart.hasUltraFreshProduct
                    }
                  />
                </div>
              )}
            </>
          )}
        </div>

        {isCartNotEmpty ? (
          <div className="mobile-only">
            <CheckoutCTA
              totalPrice={totalPrice}
              totalEconomy={shoppingCart.totalEconomy}
              weight={shoppingCart.totalWeight}
            >
              {shouldRedirectToSuggestionsPage && (
                <Link href="/pages/suggestions" legacyBehavior>
                  <Button hasMinWidth={false} variant="primary">
                    {tCart('continueToDelivery')}
                  </Button>
                </Link>
              )}
            </CheckoutCTA>
          </div>
        ) : null}
        {!shoppingCart.hasFreshProduct &&
          !shoppingCart.hasUltraFreshProduct && (
            <div className="tw-flex tw-justify-center tw-p-6">
              <p className="tw-lf-body-regular-sm tw-text-xs">
                {tCart.rich('freeDeliveryExceptions', {
                  a: (children) => (
                    <a
                      href="https://airtable.com/appcrmkLQdkxvR4Rr/shrNxPczIGy2W875s"
                      className="tw-lf-link-sm tw-text-xs"
                    >
                      {children}
                    </a>
                  ),
                })}
              </p>
            </div>
          )}
      </div>
      <style jsx>{`
        .container {
          margin: var(--space-8) 0;
        }

        .content {
          width: 100%;
          max-width: 900px;
          flex: 2;
        }

        .items-top > :global(div) {
          margin: 0;
        }

        .items-top :global(.slice-container),
        .items-top :global(.slice-container-div) {
          margin: 0;
        }
        .cart {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
        }

        .progress > :global(div),
        .hidden-price {
          padding: var(--space-4);
        }

        .hidden-price {
          font-weight: var(--font-weight-bold);
          text-align: center;
          margin-bottom: var(--space-2);
        }

        .link > :global(span) {
          margin-right: var(--space-1);
        }

        .products {
          flex: 3;
        }

        .global-information {
          flex: 1;
          // margin-left: var(--space-5);
        }

        .global-information > :global(*) {
          margin-bottom: var(--space-5);
        }

        .global-information > :global(*):last-child {
          margin-bottom: 0;
        }

        .global-informations-child {
          margin-top: var(--space-5);
        }

        :global(.summary-container) {
          max-width: 100% !important;
        }

        .right-column {
          position: sticky;
          top: 200px;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 384px;
        }

        .right-column > :global(*) {
          margin-bottom: var(--space-5);
        }

        .cart-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: var(--space-3);
        }

        .empty-cart,
        .weight,
        .progress,
        .total,
        .hidden-price {
          display: none;
        }

        .error-centered {
          text-align: center;
        }

        .recommendations {
          margin-top: var(--space-8);
        }

        @media screen and (max-width: 800px) {
          :global(#main-footer) {
            padding-bottom: 5em;
          }

          .items-top {
            margin: 0 var(--space-4);
          }

          .payment-info-mobile {
            padding: var(--space-4);
          }

          .payment-info-mobile * {
            line-space: var(--space-2);
          }

          .container {
            margin: 0 auto;
            min-height: 100vh;
            padding: 0;
            max-width: 900px;
          }

          .cart {
            margin: 0;
          }

          .content {
            margin: 0 auto;
            display: flex;
            flex-direction: column;
          }

          .cart-container {
            min-height: inherit;
            padding-bottom: calc(
              var(--mobile-checkout-cta-height) +
                ${shoppingCart.totalWeight >= maxWeight ? '60px' : '10px'}
            );
          }

          .title-block {
            text-align: center;
            padding: var(--space-4) 0;
            position: sticky;
            top: 0;
            background: var(--white);
            margin-bottom: 0;
            width: 100%;
            grid-template-columns: 1fr;
            grid-template-areas: 'title close-cart';
            border-bottom: 1px solid var(--clr-tertiary-2);
          }

          .title-block > h1 {
            grid-area: title;
            text-transform: capitalize;
            font-size: var(--font-size-2);
          }

          .hidden-price {
            display: block;
          }

          .close-cart {
            grid-area: close-cart;
            display: block;
            position: absolute;
            top: 5px;
            right: var(--space-5);
            border: none;
            background-color: transparent;
          }

          .nb-items {
            display: inline-block;
          }

          .shopping-icon,
          .global-information {
            display: none;
          }

          .cart {
            flex-direction: column;
          }

          .progress,
          .total {
            display: flex;
            justify-content: center;
          }

          .total {
            background-color: var(--white);
            padding-top: var(--space-6);
          }

          .empty-cart {
            display: block;
            margin: var(--space-4) var(--space-4) 0;
          }

          .weight {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 0 var(--space-5);
          }

          .weight > p:last-child {
            font-weight: var(--font-weight-bold);
            min-width: max-content;
            margin-left: var(--space-3);
          }

          .weight-divider {
            margin: 0 var(--space-4) var(--negative-space-5);
          }

          .recommendations {
            margin-top: var(--space-5);
          }

          .recommendations :global(.container) {
            margin-top: 0;
          }

          .guarantees {
            padding: 0 var(--padding-layout-horizontal-sm); // @todo: handle paddings and margin in the layout of the cart page
          }
        }
      `}</style>
    </div>
  );
};

export default ShoppingCart;
